import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Inbox, Trash2, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import AssignmentDialog from "../components/AssignmentDialog";
import { useCaptureContext } from "../context/CaptureContext";
import { useAuth } from "../context/AuthContext";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { formatRelativeTime, entryThumbSrc } from "../lib/media";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";

/**
 * Iter-85 — Unassigned queue with soft-delete archive.
 *
 * Tapping the dustbin no longer hard-deletes. We move the entry to a
 * dedicated Archive (one POST) and surface a clear undo affordance via
 * the toast. Permanent delete is only reachable from the archive view
 * with a strong "cannot be undone" confirmation.
 */
export default function UnassignedPage() {
    const { projectId } = useParams();
    const { user } = useAuth();
    const capture = useCaptureContext();
    const routeBase = useRouteBase();
    const apiBase = useApiBase();

    const [project, setProject] = useState(null);
    const [entries, setEntries] = useState([]);
    const [archiveCount, setArchiveCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    // Iter-85 — entry queued for the soft-archive confirm dialog.
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [archiving, setArchiving] = useState(false);

    const load = async () => {
        try {
            const [{ data: p }, { data }, { data: arch }] = await Promise.all([
                api.get(`${apiBase}/${projectId}`),
                api.get(`${apiBase}/${projectId}/state-entries`, {
                    params: { assigned: false },
                }),
                api.get(`${apiBase}/${projectId}/state-entries`, {
                    params: { assigned: false, archived: true },
                }),
            ]);
            setProject(p);
            setEntries(data);
            setArchiveCount(arch.length);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const canManage = isOwner || isAdmin;

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const openAssign = (entry) => {
        setSelected(entry);
        setDialogOpen(true);
    };

    const onSaved = (updated) => {
        setEntries((prev) => prev.filter((e) => e.id !== updated.id));
        capture?.refreshCount();
    };

    const confirmArchive = async () => {
        if (!archiveTarget) return;
        const target = archiveTarget;
        setArchiving(true);
        try {
            await api.post(
                `${apiBase}/${projectId}/state-entries/${target.id}/archive`
            );
            setEntries((prev) => prev.filter((e) => e.id !== target.id));
            setArchiveCount((c) => c + 1);
            capture?.refreshCount();
            toast.success("Moved to Unassigned Archive", {
                description: "It can be restored or permanently deleted from there.",
                action: {
                    label: "Open archive",
                    onClick: () => {
                        window.location.assign(
                            `${routeBase}/${projectId}/unassigned/archive`
                        );
                    },
                },
            });
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setArchiving(false);
            setArchiveTarget(null);
        }
    };

    return (
        <div data-testid="unassigned-page" className="min-h-screen">
            <ProjectHeader projectName={"Unassigned"} subtitle="Images without a scene" />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                <div className="mb-6 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-amber-400/15 text-amber-300 flex items-center justify-center">
                        <Inbox size={22} />
                    </div>
                    <div>
                        <h1 className="font-display text-4xl font-black text-white leading-none">
                            Unassigned
                        </h1>
                    </div>
                </div>

                {/* Iter-85 — archive entry-point. Always visible (even when
                    count = 0) so the workflow is discoverable. */}
                <Link
                    data-testid="unassigned-archive-link"
                    to={`${routeBase}/${projectId}/unassigned/archive`}
                    className="tap mb-5 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-card/60 hover:bg-white/5 px-4 py-3"
                >
                    <span className="flex items-center gap-3">
                        <Archive size={16} className="text-neutral-400" />
                        <span>
                            <span className="block text-sm text-white font-semibold">
                                Unassigned Archive
                            </span>
                            <span className="block text-[11px] text-neutral-400">
                                Soft-deleted images. Restore or permanent
                                delete from here.
                            </span>
                        </span>
                    </span>
                    <span
                        data-testid="unassigned-archive-count"
                        className="text-[10px] uppercase tracking-[0.2em] text-neutral-300 bg-secondary border border-white/10 rounded-full px-2 py-1"
                    >
                        {archiveCount}
                    </span>
                </Link>

                {error ? (
                    <p data-testid="unassigned-error" className="text-sm text-red-400 mb-4">
                        {error}
                    </p>
                ) : null}

                {loading ? (
                    <ul className="grid grid-cols-3 gap-3" data-testid="unassigned-loading">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <li
                                key={i}
                                className="aspect-square rounded-xl bg-card border border-white/10 animate-pulse"
                            />
                        ))}
                    </ul>
                ) : entries.length === 0 ? (
                    <div
                        data-testid="unassigned-empty"
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        <p className="text-sm text-neutral-400">
                            Nothing waiting. Captures with skipped assignment will land here.
                        </p>
                    </div>
                ) : (
                    <ul data-testid="unassigned-list" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {entries.map((e) => {
                            const canArchive = e.created_by === user?.id || canManage;
                            return (
                                <li
                                    key={e.id}
                                    data-testid={`unassigned-card-${e.id}`}
                                    className="relative rounded-xl overflow-hidden border border-white/10 bg-card"
                                >
                                    <button
                                        type="button"
                                        data-testid={`unassigned-open-${e.id}`}
                                        onClick={() => openAssign(e)}
                                        className="tap block w-full text-left"
                                    >
                                        <img
                                            src={entryThumbSrc(projectId, e)}
                                            alt="unassigned"
                                            className="w-full aspect-square object-cover"
                                        />
                                        <div className="p-2">
                                            <div className="text-[10px] text-neutral-400 truncate">
                                                {e.created_by_name || "Someone"}
                                            </div>
                                            <div className="text-[10px] text-neutral-500 truncate">
                                                {formatRelativeTime(e.created_at)}
                                            </div>
                                            {/* Iter-156F — recoverable-orphan
                                                breadcrumb so the user knows
                                                what this image USED to be
                                                attached to. */}
                                            {e.needs_reassignment ? (
                                                <div
                                                    data-testid={`unassigned-needs-review-${e.id}`}
                                                    className="mt-1 text-[10px] text-amber-300/90 truncate"
                                                    title="Needs review — the scene this image was attached to no longer exists. Tap to re-assign."
                                                >
                                                    Needs review
                                                    {e.original_scene_number
                                                        ? ` · was Scene ${e.original_scene_number}`
                                                        : ""}
                                                    {e.original_episode
                                                        ? ` · EP ${e.original_episode}`
                                                        : ""}
                                                </div>
                                            ) : null}
                                        </div>
                                    </button>
                                    {canArchive ? (
                                        <button
                                            type="button"
                                            data-testid={`unassigned-archive-btn-${e.id}`}
                                            onClick={() => setArchiveTarget(e)}
                                            title="Delete image"
                                            aria-label="Delete image"
                                            className="tap absolute top-2 right-2 h-7 w-7 rounded-md bg-red-600/85 text-white hover:bg-red-500 border border-red-400/40 inline-flex items-center justify-center shadow-sm"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}

                <p
                    data-testid="unassigned-warning"
                    className="mt-6 text-xs text-amber-300/80 text-center"
                >
                    Images saved here are visible to the whole team, but aren&apos;t in any
                    scene stream until you assign them.
                </p>
            </main>

            {selected ? (
                <AssignmentDialog
                    projectId={projectId}
                    open={dialogOpen}
                    mode="edit"
                    initialEntry={selected}
                    onClose={() => {
                        setDialogOpen(false);
                        setSelected(null);
                    }}
                    onSaved={onSaved}
                />
            ) : null}

            {/* Iter-117 PART 8 — danger-styled soft-delete confirmation.
                Copy mirrors the user-facing word "Delete" while still
                making the recoverable nature obvious. */}
            <AlertDialog
                open={Boolean(archiveTarget)}
                onOpenChange={(o) => !o && setArchiveTarget(null)}
            >
                <AlertDialogContent data-testid="unassigned-archive-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-400">
                            Delete this image?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            It will be moved to the Unassigned Archive.
                            You can restore it later — nothing is
                            permanently deleted yet.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="unassigned-archive-confirm-cancel"
                            disabled={archiving}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="unassigned-archive-confirm-go"
                            onClick={confirmArchive}
                            disabled={archiving}
                            className="bg-red-600 text-white hover:bg-red-500 font-semibold"
                        >
                            {archiving ? "Deleting…" : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
